import { Save24Regular, Send24Regular, DocumentBulletList20Filled } from "@fluentui/react-icons";
import { useEffect, useState, useRef } from "react";
import TestSelection from "./TestSelection";

import TestID from "./TestID";
import OiComments from "./OiComments";
import DateTimePicker, { DateTimeData } from "./DateTimePicker";
import OiTags from "./OiTags";
import OiDmc from "./OiDmc";
import OiStaff from "./OiStaff";
import OiFiles from "./OIFiles";
import OiPageHeader, { OiHeaderButton } from "../OiPageHeader";
import { PostReport, PatchReport, GetPOIReportByGuid } from "../../../services/OiService";
import { email } from "../../../App";
import { useToast } from "../../ToastProvider";

import { FileReference } from "../OiReport";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEventReport } from "../redux/ReduxContext";
import { EventReport } from "../redux/eventReport";

export const fadeIn = `
@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}
`;

export interface SearchResult {
    path: string[];
    fullPath: string;
    isLeaf: boolean;
}

export interface OiFormRef {
    hasUnsavedChanges: boolean;
    checkUnsavedChanges: () => Promise<boolean>;
    resetUnsavedChanges: () => void;
}

interface OiFormProps {

    onOpenFileGallery?: (files: FileReference[], initialIndex: number) => void;
}

export default function OiForm({ onOpenFileGallery }: OiFormProps) {
    const { showToast } = useToast();
    const { uuid } = useParams<{ uuid: string }>();
    const { state, dispatch } = useEventReport();
    const { testName } = state;
    const navigate = useNavigate();
    const location = useLocation();
    const { key } = location;
    const [mode, setMode] = useState<"Create" | "Edit" | "Clone">("Create");

    useEffect(() => {
        if (location.pathname === `/Oi/editEventReport/${uuid}`) {
            setMode("Edit");
            if (uuid) {
                hydrateForm(uuid);
            }

        }
        else if (location.pathname === `/Oi/eventReportClone/${uuid}`) {
            setMode("Clone");
            if (uuid) {
                hydrateForm(uuid);
            }
        }
        else if (location.pathname === `/Oi/eventReport/new`) {
            setMode("Create");
            if (email) {
                dispatch({
                    type: 'CLEAR_STATE',
                    payload: { author: email }
                });
            }
        }
    }, [key]);

    const hydrateForm = (uuid: string) => {
        GetPOIReportByGuid(uuid).then(report => {
            if (report) {
                const data: EventReport = report;
                dispatch({ type: 'HYDRATE', payload: data });
                console.log("Loaded report data:", data);
            }
        });
    }

    const formatDateTime = (dateData: DateTimeData): string => {
        if (!dateData.date || !dateData.time) return "";

        const isoString = `${dateData.date}T${dateData.time}:00`;
        const dateTime = new Date(isoString);

        if (isNaN(dateTime.getTime())) {
            console.error("Invalid date:", isoString);
            return "";
        }
        return dateTime.toISOString();
    };

    const validateMandatoryFields = (): boolean => {
        const hasValidTestSelection = state.testSelection !== "";
        const hasTestId = state.testName.trim() !== "";
        const hasStartDateTime = state.startTime !== "";
        const hasEndDateTime = state.endTime !== "";
        return !!(hasValidTestSelection && hasTestId && hasStartDateTime && hasEndDateTime);
    };

    const areButtonsDisabled = !validateMandatoryFields();

    const save = async (isDraft: boolean) => {
        if (mode === "Create" || mode === "Clone") {
            const payload: EventReport = state;
            payload.isDraft = isDraft;
            const response = await PostReport(payload);
            const parsed = JSON.parse(response || "{}");
            navigate(`/Oi/editEventReport/${parsed.uuid}`);
        }
        else if (mode === "Edit") {
            const payload: EventReport = state;
            payload.isDraft = isDraft;
            const response = await PatchReport(payload)
            navigate(`/Oi/eventReport/${uuid}`);
        }
    }

    return (

        <div className="w-full max-w-none mx-6 pb-4 text-primary-dark dark:text-primary-light" style={{ animation: "fadeIn 1s forwards" }}>
            <style>{fadeIn}</style>
            <OiPageHeader
                icon={<DocumentBulletList20Filled />}
                title={mode === "Edit" ? "Edit event report" : mode === "Clone" ? "Clone event report" : "New event report"}
                id={mode === "Create" ? undefined : testName}
                createdAt={mode === "Edit" ? state.createdAt : undefined}
                actions={<>
                    {uuid && <OiHeaderButton variant="secondary" onClick={() => navigate(-1)}>Back</OiHeaderButton>}
                    <OiHeaderButton variant="secondary" icon={<Save24Regular />} onClick={() => save(true)} disabled={areButtonsDisabled}>Save draft</OiHeaderButton>
                    <OiHeaderButton icon={<Send24Regular />} onClick={() => save(false)} disabled={areButtonsDisabled}>Submit</OiHeaderButton>
                </>}
            />

            <div className=" pt-1 max-lg:w-full grid grid-cols-2 max-lg:grid-cols-1 h-auto  gap-4">
                <div className="">
                    <TestSelection />
                    <TestID />

                    <div className="bg-white dark:bg-oi-dark-background grid  w-full grid-cols-2  mb-4 drop-shadow-md">
                        <DateTimePicker
                            onDateTimeChange={(startDateTimeData) => dispatch({ type: 'SET_FIELD', payload: { field: 'startTime', value: formatDateTime(startDateTimeData) } })}
                            dateTime={state.startTime ? state.startTime : ""}
                            title="Start Date and Time"
                            isRequired={true}
                        />
                        <DateTimePicker
                            onDateTimeChange={(endDateTimeData) => dispatch({ type: 'SET_FIELD', payload: { field: 'endTime', value: formatDateTime(endDateTimeData) } })}
                            dateTime={state.endTime ? state.endTime : ""}
                            title="End Date and Time"
                            isRequired={true}
                        />
                    </div>
                    <OiComments />

                    <div className="bg-white dark:bg-oi-dark-background grid max-xl:grid-cols-1 grid-cols-2  mb-4 drop-shadow-md">
                        <DateTimePicker
                            onDateTimeChange={(taggedElementsStartData) => dispatch({ type: 'UPDATE_ARRAY_ITEM', payload: { field: 'taggedElementsStart', value: formatDateTime(taggedElementsStartData), index: 0 } })}
                            dateTime={state.taggedElementsStart?.[0] ? state.taggedElementsStart[0] : ""}
                            title="Tagged Elements Start Date and Time"
                        />
                        <DateTimePicker
                            onDateTimeChange={(taggedElementsEndData) => dispatch({ type: 'UPDATE_ARRAY_ITEM', payload: { field: 'taggedElementsEnd', value: formatDateTime(taggedElementsEndData), index: 0 } })}
                            dateTime={state.taggedElementsEnd?.[0] ? state.taggedElementsEnd[0] : ""}
                            title="Tagged Elements End Date and Time"
                        />
                    </div>

                </div>

                <div className="flex-1 container  min-h-0 h-full bg-primary-ligt max-lg:mt-4">
                    <OiTags />
                    <OiDmc />
                    <OiStaff />
                    <OiFiles onOpenFileGallery={onOpenFileGallery} />
                    <div className="h-12"></div>
                </div>


            </div>
        </div>


    );
}